import axios from 'axios';
import * as R from 'ramda';
import { normalize } from 'normalizr';
import message from 'antd/lib/message';
import { batchActions } from 'redux-batched-actions';

import * as api from '../../api/apiko-api';
import * as schemes from '../../api/schemes';
import { getResErrorMsg } from '../../utils';
import * as actions from '../actions/tasks';
import * as requestsActions from '../actions/requests';
import * as contactsActions from '../actions/contacts';
import * as commentsActions from '../actions/comments';
import * as requestLogsActions from '../actions/request-logs';
import * as meetingNotesActions from '../actions/meeting-notes';


const getNextPage = ({ tasks: { page } }) => page + 1;

const getTasksFilters = ({ tasks: { filters: { dateRange, assignee } } }) => ({
  start: dateRange[0] && dateRange[0].toISOString(),
  end: dateRange[1] && dateRange[1].toISOString(),
  assignee: assignee[0] && assignee.join(),
});

const commonActions = (entities, overview) => [
  requestsActions.fetchRequestsSuccess({
    requests: entities.requests || {},
    notNewPage: true,
  }),
  contactsActions.fetchContactsSuccess({
    contacts: entities.contacts || {},
    notNewPage: true,
  }),
  meetingNotesActions.fetchMeetingNotesSuccess({
    meetingNotes: entities.meetingNotes || {},
    notNewPage: true,
    overview,
  }),
  commentsActions.fetchCommentsSuccess(entities.comments),
  requestLogsActions.fetchRequestLogsSuccess({
    logs: entities.logs || {},
  }),
];


/**
 * fetchTaskById :: ID -> Func -> Promise Error _
 */
export const fetchTaskById = taskId => dispatch =>
  Promise.resolve(dispatch(actions.fetchTasksRequest()))
    .then(() => api.fetchTaskById(taskId))
    .then((task) => {
      const { result, entities } = normalize(
        task,
        schemes.task,
      );

      return dispatch(batchActions([
        actions.fetchTasksSuccess({
          ids: [result],
          notNewPage: true,
          existsMore: false,
          tasks: entities.tasks,
        }),
        ...commonActions(entities),
      ]));
    })
    .catch(() => dispatch(actions.fetchTasksFailure()));


/**
 * fetchTasks :: (Object, Boolean) -> (Func, Func) -> Promise Error _
 */
export const fetchTasks = (cancelToken, polling = false) => (dispatch, getState) =>
  Promise.resolve(polling ? true : dispatch(actions.fetchTasksRequest()))
    .then(() => api.fetchTasks({
      page: polling ? getState().tasks.page : getNextPage(getState()),
      ...getTasksFilters(getState()),
      cancelToken,
      polling,
    }))
    .then(({ data, responseTime }) => {
      const { result, entities } = normalize(
        data,
        schemes.tasks,
      );

      dispatch(batchActions([
        actions.fetchTasksSuccess({
          polling,
          ids: result.tasks,
          count: result.count,
          tasks: entities.tasks,
          existsMore: result.existsMore,
          notNewPage: polling,
        }),
        ...commonActions(entities),
      ]));

      return responseTime;
    })
    .catch((e) => {
      if (axios.isCancel(e)) return;

      if (!polling) dispatch(actions.fetchTasksFailure());
    });


/**
 * fetchOverviewTasks :: (Object, Boolean) -> Func -> Promise Error _
 */
export const fetchOverviewTasks = (cancelToken, polling = false) => dispatch =>
  Promise.resolve(polling ? true : dispatch(actions.fetchTasksRequest()))
    .then(() => api.fetchOverviewTasks(cancelToken))
    .then(({ data, responseTime }) => {
      const { result, entities } = normalize(
        { tasks: data },
        schemes.tasks,
      );

      dispatch(batchActions([
        actions.fetchTasksSuccess({
          existsMore: result.existsMore,
          ids: result.tasks,
          tasks: entities.tasks,
          notNewPage: polling,
          overview: true,
        }),
        ...commonActions(entities, true),
      ]));

      return responseTime;
    })
    .catch((e) => {
      if (axios.isCancel(e)) return;

      if (!polling) dispatch(actions.fetchTasksFailure());
    });


/**
 * fetchCompletedTasks :: (Object, Boolean) -> (Func, Func) -> Promise Error _
 */
export const fetchCompletedTasks = (cancelToken, polling = false) => (dispatch, getState) =>
  Promise.resolve(polling ? true : dispatch(actions.fetchTasksRequest()))
    .then(() => api.fetchCompletedTasks({
      page: polling ? getState().tasks.page : getNextPage(getState()),
      ...getTasksFilters(getState()),
      cancelToken,
      polling,
    }))
    .then(({ data, responseTime }) => {
      const { result, entities } = normalize(
        data,
        schemes.tasks,
      );

      dispatch(batchActions([
        actions.fetchTasksSuccess({
          ids: result.tasks,
          count: result.count,
          tasks: entities.tasks,
          existsMore: result.existsMore,
          notNewPage: polling,
        }),
        ...commonActions(entities),
      ]));

      return responseTime;
    })
    .catch((e) => {
      if (axios.isCancel(e)) return;

      if (!polling) dispatch(actions.fetchTasksFailure());
    });


/**
 * handleTaskCreate :: Object -> Func -> Promise Error _
 */
export const handleTaskCreate = data => dispatch =>
  Promise.resolve(dispatch(actions.createTaskRequest()))
    .then(() => api.createTask(data))
    .then(({ task }) => {
      const { entities } = normalize(
        { tasks: [task] },
        schemes.tasks,
      );

      if (task.request) {
        dispatch(batchActions([
          requestsActions.addRequestTask({
            requestId: task.request._id,
            taskId: task._id,
          }),
          ...commonActions(entities),
        ]));
      }

      // eslint-disable-next-line max-len
      return dispatch(actions.createTaskSuccess(R.path([task._id], entities.tasks)));
    })
    .catch((error) => {
      dispatch(actions.createTaskFailure());
      message.error(`Fail to create task: ${getResErrorMsg(error)}`);
      return Promise.reject(error);
    });

/**
 * handleMultipleRequestTasksCreate :: (ID, Object) -> Func -> Promise Error _
 */
export const handleMultipleRequestTasksCreate = (requestId, data) => dispatch =>
  Promise.resolve(dispatch(actions.createTaskRequest()))
    .then(() => api.createMultipleRequestTasks(requestId, data))
    .then((tasks) => {
      const { entities } = normalize(
        { tasks },
        schemes.tasks,
      );

      return dispatch(batchActions([
        ...tasks.map(task => (
          requestsActions.addRequestTask({
            requestId: task.requestId,
            taskId: task._id,
          })
        )),
        ...tasks.map(task => actions.createTaskSuccess(R.path([task._id], entities.tasks))),
        ...commonActions(entities),
      ]));
    })
    .catch((error) => {
      dispatch(actions.createTaskFailure());
      message.error(`Fail to create multiple tasks: ${getResErrorMsg(error)}`);
      return Promise.reject(error);
    });


/**
* handleTaskUpdate :: (ID, Object, Boolean) -> Func -> Promise Error _
*/
export const handleTaskUpdate = (taskId, modifier, continueUpdating) => dispatch =>
  Promise.resolve(dispatch(actions.updateTaskRequest()))
    .then(() => api.updateTask(taskId, modifier))
    .then(({ task }) => {
      const taskFiltered = task.assignedTo
        ? task
        : { ...task, assignedTo: false };

      dispatch(actions.updateTaskSuccess({
        ...taskFiltered,
        continueUpdating,
      }));

      return task;
    })
    .catch((error) => {
      dispatch(actions.updateTaskFailure());
      message.error(`Failed to update task: ${getResErrorMsg(error)}`);
      return Promise.reject(error);
    });


/**
 * handleTaskOptimisticUpdate :: (ID, Object) -> (Func, Func) -> Promise Error _
 */
export const handleTaskOptimisticUpdate = (taskId, modifier) =>
  (dispatch, getState) => {
    const taskFromState = R.path(['tasks', 'items', taskId], getState());

    // Optimistic update
    dispatch(actions.updateTaskSuccess({
      _id: taskId,
      ...modifier,
    }));

    dispatch(actions.updateTaskRequest());

    return api.updateTask(taskId, modifier)
      .then(({ task }) => {
        dispatch(actions.updateTaskSuccess(task));
        message.success('Task successfully updated.');

        return task;
      })
      .catch((error) => {
        // Set prev version of Task on error
        dispatch(actions.updateTaskSuccess(taskFromState));

        message.error(`Fail to update task: ${getResErrorMsg(error)}`);
      });
  };


/**
 * handleTaskOptimisticUpdate :: [ID] -> Func -> Promise Error _
 * handler for updating tasks priority @idsList - array with ids of tasks to update in new order
 */
export const handleManyTasksUpdate = idsList => dispatch =>
  Promise.resolve(dispatch(actions.updateTaskRequest()))
    .then(() => api.updateManyTasks(idsList))
    .then(({ task }) => {
      message.success('Tasks order successfully updated.');
      // @isMultipleTasks - parameter that allow us to show spinner even after this request is done
      return dispatch(actions.updateTaskSuccess({
        idsList: task,
        isMultipleTasks: true,
      }));
    })
    .catch((error) => {
      dispatch(actions.updateTaskFailure());
      message.error(`Fail to update tasks order: ${getResErrorMsg(error)}`);
      return Promise.reject(error);
    });


/**
 * handleTaskDelete :: (ID, Number, Object) -> Func -> Promise Error _
 */
export const handleTaskDelete = (taskId, tasksLength, meetingNote = {}) => dispatch =>
  Promise.resolve(dispatch(actions.deleteTaskRequest()))
    .then(() => api.deleteTask(taskId))
    .then(({ task }) => {
      message.success('Task removed');

      const updatedMeetingNote = {
        ...meetingNote,
        meetingTasks: !R.isEmpty(meetingNote) &&
          meetingNote.meetingTasks.filter(id => id !== task._id),
      };

      dispatch(batchActions([
        actions.deleteTaskSuccess(task),
        ...(task.requestId ?
          [requestsActions.removeRequestTask({
            requestId: task.requestId,
            taskId: task._id,
          })] : []),
        (!R.isEmpty(meetingNote) &&
          meetingNotesActions.updateMeetingNoteSuccess(updatedMeetingNote)),
      ]));

      return task;
    })
    .catch((error) => {
      dispatch(actions.deleteTaskFailure());
      message.error(`Failed to delete comment: ${getResErrorMsg(error)}`);
    });



// WEBPACK FOOTER //
// ./src/redux/thunk/tasks.js